import { Image } from '@educator/components/Image'
import { SubjectCarousel, SectionTitle } from '@mc/educator-design-system'
import type { SubjectInstructor } from '../../../../types/subjects'
import { SectionContainer } from '../../SectionContainer'

interface InstructorCardProps {
  instructor: SubjectInstructor
  variant: 'grid' | 'carousel'
}

const InstructorCard = ({ instructor, variant }: InstructorCardProps) => {
  const isGrid = variant === 'grid'
  return (
    <article className={`flex flex-col gap-3 ${isGrid ? 'w-full' : 'w-72'}`}>
      <div
        className={`relative overflow-hidden bg-neutral-800 ${
          isGrid ? 'aspect-square w-full' : 'aspect-[3/4] w-72'
        }`}
      >
        <Image
          src={instructor.imageUrl}
          alt={instructor.name}
          fill
          className='object-cover'
        />
        {instructor.isChicago && (
          <Image
            src='https://static.masterclass.com/chicago-booth.png'
            alt='Chicago Booth'
            width={92}
            height={16}
            className='absolute bottom-[6px] left-[6px]'
          />
        )}
      </div>
      <div className='flex flex-col gap-1.5'>
        <h3
          className={`m-0 font-normal leading-tight text-white ${
            isGrid ? 'text-xl' : 'text-2xl'
          }`}
        >
          {instructor.name}
        </h3>
        <p className='m-0 text-sm font-normal leading-snug text-white/70'>
          {instructor.bio}
        </p>
      </div>
    </article>
  )
}

interface SubjectInstructorsSectionProps {
  sectionTitle: string
  instructors: SubjectInstructor[]
}

export const SubjectInstructorsSection = ({
  sectionTitle,
  instructors,
}: SubjectInstructorsSectionProps) => {
  const visibleInstructors = instructors.filter((i) => !i.missingImage)

  if (visibleInstructors.length === 0) return null

  return (
    <section
      data-section='subject-instructors'
      aria-label={sectionTitle}
      className='w-full overflow-hidden bg-black py-10'
    >
      <div className='md:hidden'>
        <SectionContainer clipX>
          <SubjectCarousel
            title={sectionTitle}
            gap={24}
            ariaLabel={sectionTitle}
            prevLabel='Previous instructor'
            nextLabel='Next instructor'
          >
            {visibleInstructors.map((instructor) => (
              <InstructorCard
                key={instructor.id}
                instructor={instructor}
                variant='carousel'
              />
            ))}
          </SubjectCarousel>
        </SectionContainer>
      </div>

      <div className='hidden md:block'>
        <SectionContainer>
          <SectionTitle title={sectionTitle} />
        </SectionContainer>
        <SectionContainer className='mt-6'>
          <div className='grid grid-cols-4 gap-8'>
            {visibleInstructors.map((instructor) => (
              <InstructorCard
                key={instructor.id}
                instructor={instructor}
                variant='grid'
              />
            ))}
          </div>
        </SectionContainer>
      </div>
    </section>
  )
}
